import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, FileText, Users, UserCircle, Handshake,
  Activity, BarChart3, Settings, Database, Shield, FileUp, Target, Search, Radar, Building2, List, History
} from 'lucide-react';

const Sidebar = ({ open, setOpen, onMobileClose }) => {
  const { isAdmin } = useAuth();
  const location = useLocation();

  const mainMenuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard-link' },
    { path: '/deals', icon: FileText, label: 'Deals', testId: 'nav-deals-link' },
    { path: '/prospects', icon: Search, label: 'Prospects', testId: 'nav-prospects-link' },
    { path: '/clients', icon: Users, label: 'Clients', testId: 'nav-clients-link' },
    { path: '/contacts', icon: UserCircle, label: 'Contacts', testId: 'nav-contacts-link' },
    { path: '/partners', icon: Handshake, label: 'Partners', testId: 'nav-partners-link' },
    { path: '/activities', icon: Activity, label: 'Activities', testId: 'nav-activities-link' },
    { path: '/reports', icon: BarChart3, label: 'Reports', testId: 'nav-reports-link' },
  ];

  const tenderScoutItems = [
    { path: '/tender-scout', icon: Radar, label: 'Tender Scout', testId: 'nav-tender-scout-link' },
    { path: '/tender-scout/companies', icon: Building2, label: 'Companies', testId: 'nav-companies-link' },
    { path: '/tender-scout/tenders', icon: List, label: 'All Tenders', testId: 'nav-tenders-link' },
    { path: '/ai-history', icon: History, label: 'AI History', testId: 'nav-ai-history-link' },
  ];

  const settingsItems = [
    { path: '/settings/targets', icon: Target, label: 'Yearly Targets', adminOnly: true },
    { path: '/settings/service-pillars', icon: Database, label: 'Service Pillars', adminOnly: true },
    { path: '/settings/users', icon: Users, label: 'Users', adminOnly: true },
    { path: '/settings/audit-logs', icon: Shield, label: 'Audit Logs', adminOnly: true },
    { path: '/settings/data-import', icon: FileUp, label: 'Data Import', adminOnly: true },
  ];

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    return (
      <NavLink
        to={item.path}
        data-testid={item.testId}
        onClick={onMobileClose}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-slate-600 hover:bg-slate-100'
        }`}
      >
        <Icon size={20} />
        {open && <span className="text-sm font-medium">{item.label}</span>}
      </NavLink>
    );
  };

  return (
    <aside
      className={`bg-white border-r border-slate-200 transition-all duration-300 ${
        open ? 'w-64' : 'w-20'
      }`}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 py-4 border-b border-slate-200">
          <img 
            src="/humancap-logo.png" 
            alt="HumanCap" 
            className="w-10 h-10 object-contain"
          />
          {open && (
            <div className="flex flex-col">
              <span className="font-semibold text-slate-900">HumanCap</span>
              <span className="text-xs text-slate-500">BD CRM</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1">
            {open && <p className="px-3 text-xs font-semibold text-slate-400 uppercase mb-2">Main Menu</p>}
            {mainMenuItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>

          <div className="space-y-1 mt-6">
            {open && <p className="px-3 text-xs font-semibold text-slate-400 uppercase mb-2">Lead Generation</p>}
            {tenderScoutItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>

          {isAdmin && (
            <div className="space-y-1 mt-6">
              {open && <p className="px-3 text-xs font-semibold text-slate-400 uppercase mb-2">Settings</p>}
              {settingsItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </div>
          )}
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
